import React, { useState, useEffect, useRef } from 'react';
import { Button, Card, Slider, Radio, Space, message, Typography, Progress, Spin } from 'antd';
import { PlayCircleOutlined, PauseCircleOutlined, SoundOutlined } from '@ant-design/icons';
import { submitPodcast, getPodcastUrl } from '../../api/learn';

const { Text, Title } = Typography;

const Step2Podcast = ({ onComplete }) => {
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [totalPlayedTime, setTotalPlayedTime] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [podcastUrl, setPodcastUrl] = useState(null);
  const [error, setError] = useState(null);
  
  const audioRef = useRef(null);
  const lastTimeRef = useRef(0);
  const playedTimeRef = useRef(0);

  useEffect(() => {
    loadPodcastUrl();
  }, []);

  // 加载播客URL
  const loadPodcastUrl = async () => {
    try {
      setLoading(true);
      const response = await getPodcastUrl();
      if (response.code === 200) {
        setPodcastUrl(response.data.podcast_url);
        setDuration(response.data.duration || 300);
      }
    } catch (err) {
      setError(err.message || '无法加载播客文件');
      message.error('播客文件加载失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 播放计时器
  useEffect(() => {
    let animationId;
    if (playing) {
      lastTimeRef.current = Date.now();
      animationId = setInterval(() => {
        const now = Date.now();
        const elapsed = (now - lastTimeRef.current) / 1000;
        lastTimeRef.current = now;
        
        playedTimeRef.current += elapsed * playbackSpeed;
        setTotalPlayedTime(playedTimeRef.current);
      }, 100);
    }
    
    return () => {
      if (animationId) {
        clearInterval(animationId);
      }
    };
  }, [playing, playbackSpeed]);

  // 最低播放时长要求(秒)
  const MIN_PLAY_DURATION = 180;

  // 检查完成状态
  useEffect(() => {
    // 累计播放时长达到20秒即算满足条件
    if (totalPlayedTime >= MIN_PLAY_DURATION && !completed) {
      setCompleted(true);
      message.success('播客播放完成!');
    }
  }, [totalPlayedTime, completed]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.playbackRate = playbackSpeed;
      audioRef.current.play().catch(err => {
        console.error('播放失败:', err);
        message.error('播放失败，请重试');
        setPlaying(false);
      });
      lastTimeRef.current = Date.now();
      setPlaying(true);
    }
  };

  const handleSpeedChange = (e) => {
    const speed = e.target.value;
    setPlaybackSpeed(speed);
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleSeek = (value) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value;
      setCurrentTime(value);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      const audioDuration = audioRef.current.duration;
      if (audioDuration && !isNaN(audioDuration)) {
        setDuration(audioDuration);
      }
    }
  };

  const handleEnded = () => {
    setPlaying(false);
    // 播放结束自动标记完成
    if (!completed) {
      setCompleted(true);
      message.success('播客播放完成!');
    }
  };

  const handlePlay = () => {
    setPlaying(true);
    lastTimeRef.current = Date.now();
  };

  const handlePause = () => {
    setPlaying(false);
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async () => {
    if (!completed) {
      message.warning('请完整播放播客');
      return;
    }

    try {
      // 使用实际播放时长和音频时长中较大的值
      const finalDuration = Math.max(totalPlayedTime, currentTime, duration);
      const response = await submitPodcast(finalDuration, playbackSpeed);
      if (response.code === 200) {
        message.success('播客学习完成!');
        onComplete();
      }
    } catch (error) {
      message.error(error.message || '提交失败');
    }
  };

  const progressPercent = completed ? 100 : Math.min(100, Math.round((totalPlayedTime / MIN_PLAY_DURATION) * 100));

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">加载播客文件中...</Text>
        </div>
      </div>
    );
  }

  if (error || !podcastUrl) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <SoundOutlined style={{ fontSize: 48, color: '#ccc' }} />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">{error || '播客文件未上传'}</Text>
        </div>
        <div style={{ marginTop: 16 }}>
          <Button onClick={loadPodcastUrl}>重新加载</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="step-podcast">
      <div className="step-header">
        <Title level={3}>步骤二:听播客</Title>
        <Text type="secondary">请完整听取六项行为准则的播客讲解</Text>
      </div>

      <Card className="podcast-card">
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* 播放控制 */}
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Button
              type="primary"
              shape="circle"
              size="large"
              icon={playing ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
              onClick={togglePlay}
              style={{ width: 80, height: 80, fontSize: 40 }}
            />
          </div>

          {/* 进度条 */}
          <div>
            <Slider
              value={currentTime}
              max={duration || 100}
              onChange={handleSeek}
              tipFormatter={formatTime}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text>{formatTime(currentTime)}</Text>
              <Text>{formatTime(duration)}</Text>
            </div>
          </div>

          {/* 播放速度 */}
          <div>
            <Text strong>播放速度:</Text>
            <Radio.Group 
              value={playbackSpeed} 
              onChange={handleSpeedChange}
              style={{ marginLeft: 16 }}
            >
              <Radio.Button value={1}>1x</Radio.Button>
              <Radio.Button value={1.25}>1.25x</Radio.Button>
              <Radio.Button value={1.5}>1.5x</Radio.Button>
            </Radio.Group>
          </div>

          {/* 学习进度 */}
          <Card size="small" style={{ background: '#f5f5f5' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>播放进度:</Text>
                <Text strong>{formatTime(currentTime)} / {formatTime(duration)}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>实际播放时长:</Text>
                <Text strong>{formatTime(totalPlayedTime)}</Text>
              </div>
              <Progress 
                percent={progressPercent} 
                status={completed ? 'success' : 'active'}
              />
              {completed && (
                <Text type="success" style={{ textAlign: 'center' }}>
                  ✓ 已满足播放时长要求，可以提交完成
                </Text>
              )}
            </Space>
          </Card>

          {/* 提交按钮 */}
          <Button
            type="primary"
            size="large"
            onClick={handleSubmit}
            disabled={!completed}
            block
          >
            {completed ? '完成学习' : '请完整播放播客'}
          </Button>
        </Space>
      </Card>

      {/* 音频元素 */}
      <audio
        ref={audioRef}
        src={podcastUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onPlay={handlePlay}
        onPause={handlePause}
        onError={(e) => {
          console.error('音频加载错误:', e);
          message.error('音频文件加载失败');
        }}
        preload="metadata"
      />
    </div>
  );
};

export default Step2Podcast;
